import React, { useState } from "react";
import { Lock, CheckCircle2, PackagePlus, LogOut, Boxes } from "lucide-react";
import { StockForm } from "./StockForm";
import { StockList } from "./StockList";
import BrandLockup from "./BrandLockup";
import useNoIndex from "../hooks/useNoIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StockAdd() {
  useNoIndex();
  const [pin, setPin] = useState(sessionStorage.getItem("kapatia_stock_pin") || "");
  const [verified, setVerified] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [tab, setTab] = useState("add");

  React.useEffect(() => {
    if (!pin) return;
    fetch(`${API}/stock/verify_pin`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pin }),
    }).then((r) => {
      if (!r.ok) {
        sessionStorage.removeItem("kapatia_stock_pin");
        setPin("");
        setVerified(false);
        setError("PIN changed. Please enter the new PIN.");
      } else {
        setVerified(true);
      }
    }).catch(() => {});
  }, [pin]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/stock/verify_pin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: input }),
      });
      if (!r.ok) { setError("Incorrect PIN. Try again."); return; }
      sessionStorage.setItem("kapatia_stock_pin", input);
      setPin(input);
      setVerified(true);
    } catch {
      setError("Could not reach server.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => { sessionStorage.removeItem("kapatia_stock_pin"); setPin(""); setVerified(false); setInput(""); };

  if (!verified) {
    return (
      <div className="grid min-h-screen place-items-center p-5" style={{ background: "var(--cream)" }}>
        <form onSubmit={login} data-testid="stock-pin-form" className="w-full max-w-xs rounded-2xl border p-7 text-center"
              style={{ background: "var(--cream-2)", borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-xl"
                style={{ background: "var(--green-100)", color: "var(--green-800)" }}><Lock size={22} /></span>
          <h1 className="mt-4 font-heading text-[22px] font-bold" style={{ color: "var(--green-950)" }}>Stock Entry</h1>
          <p className="mt-1 text-[13.5px]" style={{ color: "var(--muted)" }}>Enter the 4-digit staff PIN</p>
          <input type="password" inputMode="numeric" maxLength={4} pattern="[0-9]*" value={input}
                 onChange={(e) => setInput(e.target.value.replace(/\D/g, ""))} placeholder="••••" autoFocus
                 data-testid="stock-pin-input"
                 className="mt-5 w-full rounded-lg border bg-white px-3.5 py-3 text-center text-[24px] tracking-[0.5em] outline-none focus:ring-4"
                 style={{ borderColor: "#d8c8af", color: "var(--green-950)" }} />
          {error && <p className="mt-2 text-[13px] font-semibold" data-testid="stock-pin-error" style={{ color: "var(--orange-700)" }}>{error}</p>}
          <button type="submit" disabled={busy || input.length !== 4} data-testid="stock-pin-submit"
                  className="mt-4 w-full rounded-full py-3 font-heading font-bold text-white disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>Unlock</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--cream)" }}>
      <header className="border-b" style={{ background: "var(--cream-2)", borderColor: "var(--line)" }}>
        <div className="mx-auto flex max-w-lg items-center justify-between px-5 py-3">
          <BrandLockup nameSize="text-[17px]" hindiSize="text-[16px]" citySize="text-[8.5px]" />
          <button onClick={logout} data-testid="stock-logout" className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 font-heading text-[12.5px] font-bold"
                  style={{ borderColor: "var(--line)", color: "var(--green-900)" }}><LogOut size={14} /> Lock</button>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-5 py-5 pb-14">
        <div className="mb-5 flex items-center gap-2 rounded-full border p-1" style={{ borderColor: "var(--line)", background: "var(--cream-2)" }}>
          <button onClick={() => { setTab("add"); setSaved(null); }} data-testid="stock-tab-add"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full py-2.5 font-heading text-[13.5px] font-bold transition-colors"
                  style={tab === "add" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
            <PackagePlus size={15} /> Add Item
          </button>
          <button onClick={() => setTab("list")} data-testid="stock-tab-list"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full py-2.5 font-heading text-[13.5px] font-bold transition-colors"
                  style={tab === "list" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
            <Boxes size={15} /> View Stock
          </button>
        </div>

        {tab === "list" ? (
          <StockList headers={{ "X-Stock-Pin": pin }} />
        ) : saved ? (
          <div className="grid place-items-center gap-3 rounded-2xl border p-10 text-center"
               data-testid="stock-success" style={{ background: "var(--cream-2)", borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
            <CheckCircle2 size={52} style={{ color: "var(--green-700)" }} />
            <h2 className="font-heading text-[22px] font-bold" style={{ color: "var(--green-950)" }}>Item saved!</h2>
            <p className="text-[14px]" style={{ color: "var(--muted)" }}>Stock item #{saved.sno} added to inventory.</p>
            <button onClick={() => setSaved(null)} data-testid="stock-add-another"
                    className="mt-2 inline-flex items-center gap-2 rounded-full px-6 py-3 font-heading font-bold text-white"
                    style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
              <PackagePlus size={17} /> Add Another Item
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border p-5" style={{ background: "var(--cream-2)", borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
            <StockForm headers={{ "X-Stock-Pin": pin }} onSuccess={setSaved} />
          </div>
        )}
      </main>
    </div>
  );
}
